import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Trash2, Plus, Edit2 } from 'lucide-react';
import { BusinessType } from '@/types';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';
import EmailSettings from './EmailSettings';

const SettingsPage: React.FC = () => {

  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  const fetchBusinessTypes = async () => {
    const { data, error } = await supabase.from('business_types').select('*').order('name');
    if (data) setBusinessTypes(data);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
  };

  const addBusinessType = async () => {
    if (!newTypeName.trim()) return;
    const { error } = await supabase.from('business_types').insert({ name: newTypeName });
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Business type added' });
      setNewTypeName('');
      fetchBusinessTypes();
    }
  };

  const updateBusinessType = async (id: string) => {
    if (!editingName.trim()) return;
    const { error } = await supabase.from('business_types').update({ name: editingName }).eq('id', id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Business type updated' });
      setEditingId(null);
      fetchBusinessTypes();
    }
  };

  const deleteBusinessType = async (id: string) => {
    const { error } = await supabase.from('business_types').delete().eq('id', id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Business type deleted' });
      fetchBusinessTypes();
    }
  };

  return (
    <div className="p-6 space-y-6">
      <EmailSettings />

      <Card>
        <CardHeader>
          <CardTitle>Business Types</CardTitle>
          <CardDescription>Manage business types for reports</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="New business type"
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
            />
            <Button onClick={addBusinessType}><Plus className="h-4 w-4 mr-2" />Add</Button>
          </div>
          <div className="space-y-2">
            {businessTypes.map((type) => (
              <div key={type.id} className="flex items-center gap-2 p-2 border rounded">
                {editingId === type.id ? (
                  <>
                    <Input value={editingName} onChange={(e) => setEditingName(e.target.value)} />
                    <Button size="sm" onClick={() => updateBusinessType(type.id)}>Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1">{type.name}</span>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(type.id); setEditingName(type.name); }}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteBusinessType(type.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
